import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from './utils/dayjs';
import deTranslation from './locales/de/translation.json';
import deForecasts from './locales/de/forecasts.json';
import deBudget from './locales/de/budget.json';
import deDashboard from './locales/de/dashboard.json';
import deNavigation from './locales/de/navigation.json';
import deAllBookings from './locales/de/allBookings.json';
import deAllDeltas from './locales/de/allDeltas.json';
import deBidding from './locales/de/bidding.json';
import deErrors from './locales/de/errors.json';
import deLouvre from './locales/de/louvre.json';
import enTranslation from './locales/en/translation.json';
import enForecasts from './locales/en/forecasts.json';
import enBudget from './locales/en/budget.json';
import enDashboard from './locales/en/dashboard.json';
import enNavigation from './locales/en/navigation.json';
import enAllBookings from './locales/en/allBookings.json';
import enAllDeltas from './locales/en/allDeltas.json';
import enBidding from './locales/en/bidding.json';
import enErrors from './locales/en/errors.json';
import enLouvre from './locales/en/louvre.json';

export const LANGUAGE_STORAGE_KEY = 'language';
export const SUPPORTED_LANGUAGES = ['de', 'en'];
export const DEFAULT_LANGUAGE = 'de';

const resources = {
    de: {
        translation: deTranslation,
        navigation: deNavigation,
        dashboard: deDashboard,
        allBookings: deAllBookings,
        allDeltas: deAllDeltas,
        bidding: deBidding,
        forecasts: deForecasts,
        budget: deBudget,
        errors: deErrors,
        louvre: deLouvre,
    },
    en: {
        translation: enTranslation,
        navigation: enNavigation,
        dashboard: enDashboard,
        allBookings: enAllBookings,
        allDeltas: enAllDeltas,
        bidding: enBidding,
        forecasts: enForecasts,
        budget: enBudget,
        errors: enErrors,
        louvre: enLouvre,
    },
};

// The choice is per-browser (localStorage), NOT browser-language detection: the
// default must stay German regardless of the visitor's OS/browser locale.
const getInitialLanguage = () => {
    try {
        const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
        return SUPPORTED_LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
    } catch {
        return DEFAULT_LANGUAGE;
    }
};

i18n.use(initReactI18next) // passes i18n down to react-i18next
    .init({
        returnNull: false,
        resources,
        lng: getInitialLanguage(),
        // Keys are the English source sentences, so a missing entry must render the key
        // itself — never fall back to another language (German text in the EN UI).
        // The i18nKeyParity test keeps the en/de files complete and in lockstep.
        fallbackLng: false,
        supportedLngs: SUPPORTED_LANGUAGES,
        // Flat natural-language keys contain '.' and ':' — disable separator parsing.
        keySeparator: false,
        nsSeparator: false,

        interpolation: {
            escapeValue: false, // react already safes from xss
        },
    });

// Keep <html lang> in sync with the UI language: it is the primary signal browser
// translation extensions use to decide whether to offer/auto-translate the page,
// and screen readers pick pronunciation from it. index.html ships lang="de" as the
// pre-boot default.
const applyLanguageToDocument = (lng) => {
    if (typeof document !== 'undefined') {
        document.documentElement.lang = lng;
    }
};

// Keep dayjs's locale in sync with the UI language so formatted month/day names
// (e.g. the MONTH_NAME_YEAR options in the export dialogs) render translated.
// SUPPORTED_LANGUAGES map 1:1 to the imported dayjs locales ('de', 'en').
const applyLanguageToDayjs = (lng) => {
    dayjs.locale(lng);
};

applyLanguageToDocument(i18n.language);
applyLanguageToDayjs(i18n.language);

// Central persistence: every changeLanguage() caller (the AppBar switcher today)
// persists for free.
i18n.on('languageChanged', (lng) => {
    applyLanguageToDocument(lng);
    applyLanguageToDayjs(lng);
    try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    } catch {
        // storage unavailable (private mode) — the choice just won't survive a reload
    }
});

export default i18n;
